import { useState, useRef, useCallback } from 'react';
import type { DriveItem } from '@/mocks/driveFiles';
import { uploadFilesWithProgress, uploadFolderWithProgress } from '@/services/driveApi';
import type { UploadItem as ApiUploadItem } from '@/services/driveApi';

interface UploadItem {
  name: string;
  relativePath: string;
  file: File;
}

interface UploadZoneProps {
  parentId: string | null;
  parentName: string;
  parentPath: string;
  onClose: () => void;
  onUpload: (items: DriveItem[]) => void;
}

function formatSpeed(bytesPerSecond: number): string {
  if (bytesPerSecond <= 0) return '0 B/s';
  if (bytesPerSecond >= 1024 * 1024) {
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
  }
  if (bytesPerSecond >= 1024) {
    return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
  }
  return `${Math.round(bytesPerSecond)} B/s`;
}

export default function UploadZone({ parentId, parentName, parentPath, onClose, onUpload }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ name: string; progress: number }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSpeed, setUploadSpeed] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const lastLoadedRef = useRef(0);
  const lastTimeRef = useRef(0);

  const doRealUpload = async (
    fileItems: UploadItem[],
    isFolder: boolean,
    names: string[]
  ) => {
    const uploadList = names.map((name) => ({ name, progress: 0 }));
    setUploadingFiles(uploadList);
    setIsUploading(true);
    setUploadError(null);
    setUploadSpeed('');
    lastLoadedRef.current = 0;
    lastTimeRef.current = 0;

    try {
      const apiItems: ApiUploadItem[] = fileItems.map((item) => ({
        name: item.name,
        relativePath: item.relativePath,
        file: item.file,
      }));

      let result: DriveItem[];
      if (isFolder) {
        result = await uploadFolderWithProgress(parentPath, apiItems, (percent, loaded) => {
          setUploadingFiles((prev) =>
            prev.map((f) => ({ ...f, progress: percent }))
          );
          const now = Date.now();
          if (lastTimeRef.current > 0) {
            const timeDelta = (now - lastTimeRef.current) / 1000;
            const bytesDelta = loaded - lastLoadedRef.current;
            if (timeDelta > 0.05 && bytesDelta > 0) {
              setUploadSpeed(formatSpeed(bytesDelta / timeDelta));
            }
          }
          lastLoadedRef.current = loaded;
          lastTimeRef.current = now;
        });
      } else {
        const files = fileItems.map((item) => item.file);
        result = await uploadFilesWithProgress(parentPath, files, (percent, loaded) => {
          setUploadingFiles((prev) =>
            prev.map((f) => ({ ...f, progress: percent }))
          );
          const now = Date.now();
          if (lastTimeRef.current > 0) {
            const timeDelta = (now - lastTimeRef.current) / 1000;
            const bytesDelta = loaded - lastLoadedRef.current;
            if (timeDelta > 0.05 && bytesDelta > 0) {
              setUploadSpeed(formatSpeed(bytesDelta / timeDelta));
            }
          }
          lastLoadedRef.current = loaded;
          lastTimeRef.current = now;
        });
      }

      setUploadingFiles((prev) =>
        prev.map((f) => ({ ...f, progress: 100 }))
      );

      setTimeout(() => {
        onUpload(result);
        onClose();
      }, 400);
    } catch (err: any) {
      setUploadError(err.message || '업로드 실패');
    }
  };

  const processFiles = (fileList: FileList) => {
    const items: UploadItem[] = Array.from(fileList).map((file) => ({
      name: file.name,
      relativePath: (file as any).webkitRelativePath || file.name,
      file,
    }));

    const isFolder = items.some((item) => item.relativePath !== item.name);
    const names = items.map((i) => i.relativePath || i.name);
    doRealUpload(items, isFolder, names);
  };

  // Recursively collect files from a directory entry
  const collectFilesFromEntry = useCallback(
    (entry: FileSystemEntry): Promise<UploadItem[]> => {
      return new Promise((resolve) => {
        if (entry.isFile) {
          const fileEntry = entry as FileSystemFileEntry;
          fileEntry.file((file) => {
            resolve([{ name: file.name, relativePath: entry.fullPath.replace(/^\//, ''), file }]);
          });
        } else if (entry.isDirectory) {
          const dirEntry = entry as FileSystemDirectoryEntry;
          const reader = dirEntry.createReader();
          const allEntries: FileSystemEntry[] = [];

          const readBatch = (): Promise<FileSystemEntry[]> => {
            return new Promise((res) => {
              reader.readEntries((entries) => {
                if (entries.length === 0) {
                  res(allEntries);
                } else {
                  allEntries.push(...entries);
                  readBatch().then(res);
                }
              });
            });
          };

          readBatch().then(async (entries) => {
            const results: UploadItem[][] = await Promise.all(
              entries.map((e) => collectFilesFromEntry(e))
            );
            resolve(results.flat());
          });
        } else {
          resolve([]);
        }
      });
    },
    []
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const { items } = e.dataTransfer;
    if (!items || items.length === 0) return;

    const uploadItems: UploadItem[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = (item as any).webkitGetAsEntry ? (item as any).webkitGetAsEntry() : null;
        if (entry) {
          const collected = await collectFilesFromEntry(entry);
          uploadItems.push(...collected);
        } else {
          const file = item.getAsFile();
          if (file) {
            uploadItems.push({ name: file.name, relativePath: file.name, file });
          }
        }
      }
    }

    if (uploadItems.length === 0) return;

    const isFolder = uploadItems.some((item) => item.relativePath !== item.name);
    const names = uploadItems.map((i) => i.relativePath || i.name);
    doRealUpload(uploadItems, isFolder, names);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
    e.target.value = '';
  };

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
    e.target.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-background-50 rounded-xl p-6 max-w-lg w-full border border-background-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground-900">파일 업로드</h2>
            <p className="text-sm text-foreground-500 mt-0.5">위치: {parentName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {!isUploading && !uploadError ? (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all duration-200 ${
              isDragging
                ? 'border-primary-400 bg-primary-50/50 scale-[1.02]'
                : 'border-background-300 hover:border-primary-300 hover:bg-background-100'
            }`}
          >
            <span className={`w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full transition-colors ${
              isDragging ? 'bg-primary-100' : 'bg-background-200'
            }`}>
              <i className={`ri-upload-cloud-2-line text-3xl ${
                isDragging ? 'text-primary-500' : 'text-secondary-400'
              }`}></i>
            </span>
            <p className="text-base font-medium text-foreground-700">
              {isDragging ? '파일을 여기에 놓으세요' : '파일을 여기에 끌어다 놓거나'}
            </p>
            <p className="text-sm text-foreground-500 mt-1">
              아래 버튼으로 파일 또는 폴더 선택
            </p>
            <p className="text-xs text-foreground-400 mt-3">
              모든 파일 형식 지원 (최대 20GB)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={folderInputRef}
              type="file"
              /* @ts-expect-error webkitdirectory is not in React types */
              webkitdirectory=""
              /* @ts-expect-error directory is not in React types */
              directory=""
              multiple
              onChange={handleFolderSelect}
              className="hidden"
            />
          </div>
        ) : uploadError ? (
          <div className="p-8 text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-red-100">
              <i className="ri-error-warning-line text-3xl text-red-500"></i>
            </span>
            <p className="text-base font-medium text-foreground-700 mb-2">업로드 실패</p>
            <p className="text-sm text-foreground-500 mb-4">{uploadError}</p>
            <button
              onClick={() => {
                setUploadError(null);
                setIsUploading(false);
                setUploadingFiles([]);
                setUploadSpeed('');
              }}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              다시 시도
            </button>
          </div>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {uploadingFiles.map((file, index) => (
              <div key={index} className="p-3 bg-background-100 rounded-lg border border-background-200">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-foreground-700 truncate max-w-[300px]">
                    {file.name}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap ml-2">
                    {file.progress < 100 ? `${Math.round(file.progress)}%` : '완료'}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      file.progress >= 100 ? 'bg-primary-500' : 'bg-primary-400'
                    }`}
                    style={{ width: `${file.progress}%` }}
                  ></div>
                </div>
                {uploadSpeed && file.progress < 100 && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <i className="ri-speed-up-line text-xs text-foreground-400"></i>
                    <span className="text-xs text-foreground-500">{uploadSpeed}</span>
                  </div>
                )}
              </div>
            ))}
            {uploadingFiles.some((f) => f.progress < 100) && (
              <p className="text-xs text-foreground-500 text-center pt-1">업로드 중...</p>
            )}
            {uploadingFiles.every((f) => f.progress >= 100) && uploadingFiles.length > 0 && (
              <p className="text-xs text-foreground-500 text-center pt-2">업로드 완료!</p>
            )}
          </div>
        )}

        {!isUploading && !uploadError && (
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => folderInputRef.current?.click()}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-background-300 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-folder-upload-line mr-1.5"></i>폴더 업로드
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-background-300 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-upload-line mr-1.5"></i>파일 선택
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-background-200 text-foreground-600 hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
          </div>
        )}
      </div>
    </div>
  );
}